import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { useAsyncCallback, useNotification } from '../../../../hooks'
import { parseToOldApiOrdering } from '../../../../utils/sort'
import { getFilesBreadcrumbs } from '../../File.utils'

import { TABLE_LOAD_INITIAL_PAGINATION } from './useFileTableLoader.constants'
import { getFileItemsFromResponse } from './useFileTableLoader.utils'

export function useFileTableLoader({
  directoryId,
  getApiProps,
  getData,
  getDataPagination,
  getItemsFromResponse = getFileItemsFromResponse,
  objectId,
}) {
  const { t } = useTranslation()
  const [files, setFiles] = useState(null)
  const [parent, setParent] = useState({ id: directoryId })
  const [count, setCount] = useState(0)
  const [dataOrdering, setDataOrdering] = useState(EMPTY_LIST)
  const [isInitLoading, setIsInitLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState(TABLE_LOAD_INITIAL_PAGINATION)

  const { handleAddErrorNotification } = useNotification()

  const apiParams = getApiProps({
    objectId,
    ordering: dataOrdering,
    page: pagination.page,
    parent: parent?.id,
  })

  const loadData = useAsyncCallback(
    params => {
      setLoading(true)

      return getData?.(params)
        .then(({ data }) => {
          setFiles(getItemsFromResponse?.(data).items)
          setParent(getItemsFromResponse?.(data).parent)
          setCount(getItemsFromResponse?.(data).count)
          setPagination(prev => {
            return {
              ...prev,
              ...getDataPagination(data),
            }
          })

          return data
        })
        .catch(error => {
          handleAddErrorNotification('Error.CommonApiRequestFailKey')

          return error
        })
        .finally(data => {
          setLoading(false)
          setIsInitLoading(false)

          return data
        })
    },
    [
      getData,
      getDataPagination,
      getItemsFromResponse,
      handleAddErrorNotification,
    ]
  )

  useEffect(() => {
    loadData(apiParams)
  }, [pagination.page, parent?.id])

  const handleUpdateOrderingData = useAsyncCallback(
    (obj = EMPTY_OBJECT) => {
      return loadData(obj)
    },
    [loadData]
  )

  function handleClickFolder(id) {
    setParent({ id })
    setPagination(TABLE_LOAD_INITIAL_PAGINATION)
  }

  function handleSetOrdering(ordering) {
    setDataOrdering(ordering)

    return handleUpdateOrderingData({
      ...apiParams,
      ...parseToOldApiOrdering(ordering),
    })
  }

  const breadcrumbsItems = getFilesBreadcrumbs({
    directoryId,
    handleClickOption: handleClickFolder,
    path: parent?.path,
    t,
  })

  return {
    breadcrumbsItems,
    count,
    dataOrdering,
    files,
    handleClickFolder,
    handleSetOrdering,
    isInitLoading,
    loading,
    pagination,
    setPagination,
  }
}
